/**
 * File: money.ts
 * Path: packages/config/src/accounting/money.ts
 * Description: Rand amounts as whole cents. The books never hold a rand
 * float: amounts are read into cents once, added as integers, and only
 * turned back into rand for display.
 */

const NBSP = "\u00a0";

const layout = (cents: number, space: string): string => {
  const safe = Number.isFinite(cents) ? Math.round(cents) : 0;
  const abs = Math.abs(safe);
  const whole = Math.floor(abs / 100)
    .toString()
    .replace(/\B(?=(\d{3})+(?!\d))/g, space);
  return `${safe < 0 ? "-" : ""}R${space}${whole},${String(abs % 100).padStart(2, "0")}`;
};

/**
 * "R 10 000,00": the one money format (Liam, 4 Nov 2026), on every admin
 * page, contract, quote, invoice and their PDFs. Laid out by hand, so the
 * browser, the server and every PDF give the same text (Intl differs between
 * runtimes). The spaces are non-breaking, so an amount never splits over two
 * lines. Negative amounts get a minus.
 */
export const formatCents = (cents: number): string => layout(cents, NBSP);

/** formatCents for a rand amount (contracts, receipts, quotes hold rand). */
export const formatRand = (rand: number): string => formatCents(randToCents(rand));

/**
 * The same format with plain spaces, for documents: PDFs (every PDF font can
 * draw a plain space) and contract text, which is hashed and must read the
 * same wherever it is built.
 */
export const printCents = (cents: number): string => layout(cents, " ");

/** printCents for a rand amount. */
export const printRand = (rand: number): string => printCents(randToCents(rand));

/**
 * "R 1 234": whole rands, only where SARS eFiling takes whole rands (the
 * tax worksheets). Everywhere else uses formatCents.
 */
export const formatWholeRand = (rand: number): string => {
  const cents = formatCents(Math.round(Number.isFinite(rand) ? rand : 0) * 100);
  return cents.slice(0, -3);
};

/** "1234.56", for exports and form fields: plain, dot decimal, no grouping. */
export const centsToPlain = (cents: number): string => {
  const sign = cents < 0 ? "-" : "";
  const abs = Math.abs(cents);
  return `${sign}${Math.floor(abs / 100)}.${String(abs % 100).padStart(2, "0")}`;
};

/** A rand amount (from contracts, receipts or quotes) in whole cents. */
export const randToCents = (rand: number): number => Math.round(rand * 100);

/**
 * Reads what a person or a bank file wrote as a rand amount: "1234.56",
 * "R 1 234,56", "1,234.56", "-50", "(50.00)" or "50.00 CR". Returns null when
 * it isn't an amount. A comma is a decimal comma only when it is the last
 * separator and has one or two digits after it.
 */
export const parseRandToCents = (input: string): number | null => {
  let text = input.trim().replace(/[\s\u00a0\u202f]/g, "");
  if (text === "" || text === "-") return null;
  let negative = false;
  if (/^\(.*\)$/.test(text)) {
    negative = true;
    text = text.slice(1, -1);
  }
  if (/cr$/i.test(text)) {
    text = text.slice(0, -2);
  } else if (/dr$/i.test(text)) {
    negative = !negative;
    text = text.slice(0, -2);
  }
  if (text.startsWith("-")) {
    negative = !negative;
    text = text.slice(1);
  }
  text = text.replace(/^R/i, "");
  if (text.startsWith("-")) {
    negative = !negative;
    text = text.slice(1);
  }
  const lastComma = text.lastIndexOf(",");
  const lastDot = text.lastIndexOf(".");
  if (lastComma > lastDot && /,\d{1,2}$/.test(text)) {
    text = text.replace(/\./g, "").replace(",", ".");
  } else {
    text = text.replace(/,/g, "");
  }
  if (!/^\d+(\.\d{1,2})?$/.test(text)) return null;
  const [whole, fraction = ""] = text.split(".");
  const cents = Number(whole) * 100 + Number(fraction.padEnd(2, "0"));
  if (!Number.isSafeInteger(cents)) return null;
  return negative ? -cents : cents;
};

/** True for a whole, non-negative number of cents the books can hold. */
export const isCents = (value: unknown): value is number =>
  typeof value === "number" && Number.isSafeInteger(value) && value >= 0;
